import {
  isWorkplaceRequestReservationFieldValue,
  parseDateOnlyString,
  WORKPLACE_REQUEST_RESERVATION_MAX_ATTENDEES,
  WORKPLACE_REQUEST_RESERVATION_MAX_DATES,
  type WorkplaceRequestReservationAttendeeInput,
  type WorkplaceRequestReservationFieldConfig,
  type WorkplaceRequestReservationFieldValue,
  type WorkplaceRequestReservationFieldValueInput
} from "@gettactic/api";
import { format } from "date-fns";
import { isValidTime } from "../date-time/isValidTime";
import { time12To24, time24To12 } from "../date-time/timeStringConverters";

/**
 * A `reservation` answer as the form holds it, and the conversions to and from
 * the shape the API takes.
 *
 * The recurrence rule travels as an opaque string: what a requester may pick
 * is a question for the composer, which owns the presets, while everything
 * here only has to carry the answer intact.
 */

const DATE_FORMAT = "yyyy-MM-dd";

export type ReservationRecurrenceFormValue = {
  /** The RRULE exactly as it will be sent, e.g. `FREQ=WEEKLY;BYDAY=TU`. */
  rrule: string;
  /** `null` for a series with no end. */
  endDate: Date | null;
};

/**
 * Days as `Date`s and times in the 12-hour form the shared picker uses — the
 * same trade the plain `date` and `time` fields make. Attendees are the plain
 * strings the picker holds: a user id, or a guest's address.
 */
export type ReservationFieldFormValue = {
  dates: Date[];
  startTime: string;
  endTime: string;
  recurrence: ReservationRecurrenceFormValue | null;
  attendees: string[];
};

export const EMPTY_RESERVATION_FORM_VALUE: ReservationFieldFormValue = {
  dates: [],
  startTime: "",
  endTime: "",
  recurrence: null,
  attendees: []
};

/**
 * Whether an attendee entry is a guest rather than somebody in the directory.
 *
 * The two are told apart by the "@": a Tactic user id never contains one, and
 * the picker only ever creates an entry from something that already parsed as
 * an address.
 */
export const isGuestAttendee = (value: string): boolean => value.includes("@");

export const toReservationAttendee = (
  value: string
): WorkplaceRequestReservationAttendeeInput =>
  isGuestAttendee(value) ? { email: value } : { user_id: value };

/** The inverse: an attendee read back as the plain string a picker holds. */
export const fromReservationAttendee = (
  attendee: WorkplaceRequestReservationAttendeeInput
): string => attendee.user_id ?? attendee.email ?? "";

/**
 * Whether a form value is a reservation answer. Form values are held as
 * `unknown`, so this is the way in for the shared read/write converters.
 */
export const isReservationFieldFormValue = (
  value: unknown
): value is ReservationFieldFormValue => {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Record<string, unknown>;
  return (
    Array.isArray(candidate.dates) &&
    Array.isArray(candidate.attendees) &&
    typeof candidate.startTime === "string" &&
    typeof candidate.endTime === "string"
  );
};

/** Nothing filled in at all — as opposed to filled in wrongly. */
export const isEmptyReservationFormValue = (
  form: ReservationFieldFormValue
): boolean => form.dates.length === 0 && !form.startTime && !form.endTime;

/**
 * The picker's 12-hour string as core's 24-hour `HH:MM`, or `null` when it is
 * neither. An answer read back and never touched is already 24-hour, which is
 * why that case is passed through rather than converted.
 */
const toApiTime = (value: string): string | null => {
  if (!value) return null;
  if (isValidTime(value)) return value;
  return isValidTime(value, true) ? time12To24(value) : null;
};

const toApiDate = (date: Date): string => format(date, DATE_FORMAT);

/** An answer read off a request, in the shape the form edits. */
export const toReservationFieldFormValue = (
  value: unknown
): ReservationFieldFormValue => {
  if (!isWorkplaceRequestReservationFieldValue(value)) {
    return EMPTY_RESERVATION_FORM_VALUE;
  }
  const endDate = value.recurrence?.end_date;
  return {
    dates: value.dates
      .map(parseDateOnlyString)
      .filter((date) => !Number.isNaN(date.getTime())),
    startTime: time24To12(value.start_time),
    endTime: time24To12(value.end_time),
    recurrence: value.recurrence
      ? {
          rrule: value.recurrence.rrule,
          endDate: endDate ? parseDateOnlyString(endDate) : null
        }
      : null,
    attendees: (value.attendees ?? [])
      .map(fromReservationAttendee)
      .filter(Boolean)
  };
};

/**
 * The answer as the field currently collects it.
 *
 * A switch turned off after a request was filed leaves an answer carrying
 * something the question no longer asks for — extra days, a repeat, a guest
 * list. Core rejects the whole value for its mere presence, and the composer
 * stops rendering it, so the requester can neither see it nor remove it.
 *
 * Reducing it here, once, is what keeps the three views honest: what the
 * composer shows, what the checks below judge, and what is finally sent all
 * describe the same booking. The same object comes back when nothing needs
 * dropping, so this is safe to call on every render.
 */
export const reservationValueAllowedBy = (
  form: ReservationFieldFormValue,
  config: WorkplaceRequestReservationFieldConfig
): ReservationFieldFormValue => {
  // The earliest day is the one the composer shows in a single-day calendar.
  const dates =
    config.multi_day_enabled || form.dates.length <= 1
      ? form.dates
      : [...form.dates].sort((left, right) => +left - +right).slice(0, 1);
  const recurrence = config.recurrence_enabled ? form.recurrence : null;
  const attendees = config.attendees_enabled ? form.attendees : [];

  const isUnchanged =
    dates === form.dates &&
    recurrence === form.recurrence &&
    attendees === form.attendees;
  return isUnchanged ? form : { ...form, dates, recurrence, attendees };
};

/**
 * The answer to send, or `null` when there is nothing to send — no days, or a
 * time missing. The caller drops a `null` the way it drops any empty answer.
 *
 * `config` is what the field currently collects. It is applied here rather
 * than trusted from the form because an admin can turn a switch off after a
 * request was filed: the composer then stops rendering that part, and without
 * this the next save would still send it and be rejected outright.
 *
 * Days are sorted, which is how core returns them anyway. Nothing else is
 * corrected — a value that breaks a rule is reported by
 * {@link reservationFieldIssues} rather than quietly repaired.
 */
export const toReservationFieldValue = (
  form: ReservationFieldFormValue,
  config: WorkplaceRequestReservationFieldConfig
): WorkplaceRequestReservationFieldValueInput | null => {
  const allowed = reservationValueAllowedBy(form, config);
  const startTime = toApiTime(allowed.startTime);
  const endTime = toApiTime(allowed.endTime);
  if (allowed.dates.length === 0 || !startTime || !endTime) return null;

  const dates = allowed.dates.map(toApiDate).sort();

  const recurrence = allowed.recurrence
    ? {
        rrule: allowed.recurrence.rrule,
        start_date: dates[0],
        end_date: allowed.recurrence.endDate
          ? toApiDate(allowed.recurrence.endDate)
          : null
      }
    : undefined;

  const attendees = allowed.attendees.map(toReservationAttendee);

  return {
    dates,
    start_time: startTime,
    end_time: endTime,
    ...(recurrence ? { recurrence } : {}),
    ...(attendees.length > 0 ? { attendees } : {})
  };
};

/**
 * What stops an answer from being submitted, in the words the form shows.
 *
 * The same rules core applies, run before the round-trip: core validates the
 * object as a whole and answers a flat `400` with no per-rule detail, so an
 * unchecked answer would fail with nothing to act on.
 */
export const reservationFieldIssues = (
  answer: ReservationFieldFormValue,
  config: WorkplaceRequestReservationFieldConfig,
  isRequired: boolean
): string[] => {
  // Judged on what the field collects today, not on what it once did: a day
  // the composer no longer shows is not one the requester can be asked about.
  const form = reservationValueAllowedBy(answer, config);

  if (isEmptyReservationFormValue(form)) {
    return isRequired ? ["Say when you need it."] : [];
  }

  const issues: string[] = [];

  if (form.dates.length === 0) issues.push("Pick at least one day.");
  if (form.dates.length > WORKPLACE_REQUEST_RESERVATION_MAX_DATES) {
    issues.push(
      `Pick at most ${WORKPLACE_REQUEST_RESERVATION_MAX_DATES} days.`
    );
  }

  const startTime = toApiTime(form.startTime);
  const endTime = toApiTime(form.endTime);
  if (!startTime) issues.push("Pick a start time.");
  if (!endTime) issues.push("Pick an end time.");
  // 24-hour `HH:MM` compares as text, and every day shares this one window.
  if (startTime && endTime && endTime <= startTime) {
    issues.push("The end time has to be after the start time.");
  }

  if (form.recurrence) {
    if (form.dates.length > 1) {
      issues.push("A repeat covers one day — remove the extra days first.");
    }
    const firstDate = [...form.dates].sort((a, b) => +a - +b)[0];
    if (
      form.recurrence.endDate &&
      firstDate &&
      toApiDate(form.recurrence.endDate) < toApiDate(firstDate)
    ) {
      issues.push("The repeat can't end before the first day.");
    }
  }

  if (form.attendees.length > WORKPLACE_REQUEST_RESERVATION_MAX_ATTENDEES) {
    issues.push(
      `Invite at most ${WORKPLACE_REQUEST_RESERVATION_MAX_ATTENDEES} people.`
    );
  }

  return issues;
};

/**
 * A reservation answer as one line of plain text, for the places a React tree
 * doesn't fit — a PDF export, a table cell.
 *
 * The recurrence is only mentioned, not spelled out: putting a rule into words
 * needs the RRULE library, which belongs with the UI that offers the repeats.
 */
export const formatReservationFieldValue = (
  value: WorkplaceRequestReservationFieldValue
): string => {
  const days = value.dates
    .map((date) => format(parseDateOnlyString(date), "EEE d MMM yyyy"))
    .join(", ");
  const parts = [
    days,
    `${time24To12(value.start_time)} – ${time24To12(value.end_time)}`
  ];
  if (value.recurrence) parts.push("repeating");
  const attendees = value.attendees?.length ?? 0;
  if (attendees > 0) {
    parts.push(`${attendees} ${attendees === 1 ? "attendee" : "attendees"}`);
  }
  return parts.join(" · ");
};

/** Minutes past midnight for a 24-hour `HH:MM` (or `HH:MM:SS`), else `null`. */
const minutesOfDay = (time: string): number | null => {
  const [hours, minutes] = time.split(":").map(Number);
  return Number.isFinite(hours) && Number.isFinite(minutes)
    ? hours * 60 + minutes
    : null;
};

/**
 * How long each day of the booking runs — `45 min`, `1 hour`, `1h 30m`.
 *
 * `null` when the times don't make a window, which is a value core wouldn't
 * have accepted: the caller leaves the duration out rather than printing
 * something wrong.
 */
export const reservationDurationLabel = (
  value: Pick<WorkplaceRequestReservationFieldValue, "start_time" | "end_time">
): string | null => {
  const start = minutesOfDay(value.start_time);
  const end = minutesOfDay(value.end_time);
  if (start === null || end === null || end <= start) return null;

  const total = end - start;
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  if (hours === 0) return `${minutes} min`;
  if (minutes === 0) return hours === 1 ? "1 hour" : `${hours} hours`;
  return `${hours}h ${minutes}m`;
};
